import { getDisplayString, getEndEpoch, getStartEpoch, } from '@thoughtspot/custom-calendar-service';
import { arrayHasDuplicates } from '@thoughtspot/js-util';
import { hasCustomCalendar, isAttribute, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getSeries, getTooltipConfig } from '../data/data-util';
import { getEnvFlags } from '../env';
import { AnswerColumnEffectiveType, ChartType, } from '../types';
import { getLegendCardinalityError } from './cardinality-validation-service';
import { getAllColumnsInAxisConfig, getChartType, getColumn, } from './chart-model-utils';
import { getChartSpecificDataValidationError, isGeoChartType, } from './chart-type-specification-service';
import { getColumnsUsedInConditionalFormatting } from './conditional-formatting-utils';
export var ErrorTypes;
(function (ErrorTypes) {
    ErrorTypes["VALIDATION_PENDING"] = "VALIDATION_PENDING";
    ErrorTypes["NO_DATA"] = "NO_DATA";
    ErrorTypes["UNIQUE_X_VALUES_ALL_DATA"] = "UNIQUE_X_VALUES_ALL_DATA";
    ErrorTypes["UNIQUE_X_VALUES_PER_SERIES"] = "UNIQUE_X_VALUES_PER_SERIES";
    ErrorTypes["ONLY_ONE_Y_AXIS_COLUMN_SUPPORTED"] = "ONLY_ONE_Y_AXIS_COLUMN_SUPPORTED";
    ErrorTypes["NON_NEGATIVE_VALUES_NEEDED"] = "NON_NEGATIVE_VALUES_NEEDED";
    ErrorTypes["PIE_NO_POSITIVE_VALUES"] = "PIE_NO_POSITIVE_VALUES";
    ErrorTypes["VIZ_BLANK_FOR_ALL_NEGATIVE_VALUES"] = "VIZ_BLANK_FOR_ALL_NEGATIVE_VALUES";
    ErrorTypes["DATA_INCOMPLETE_PIVOT"] = "DATA_INCOMPLETE_PIVOT";
    ErrorTypes["TREEMAP_CARDINALITY_ERROR"] = "TREEMAP_CARDINALITY_ERROR";
    ErrorTypes["SANKEY_CARDINALITY_ERROR"] = "SANKEY_CARDINALITY_ERROR";
    ErrorTypes["SPIDER_WEB_CARDINALITY_ERROR"] = "SPIDER_WEB_CARDINALITY_ERROR";
    ErrorTypes["LEGEND_CARDINALITY_ERROR"] = "LEGEND_CARDINALITY_ERROR";
    ErrorTypes["FAILED_TO_RENDER_VIZ"] = "FAILED_TO_RENDER_VIZ";
    ErrorTypes["CHART_NOT_AVAILABLE"] = "CHART_NOT_AVAILABLE";
    ErrorTypes["MISSING_GEO_CONFIG"] = "MISSING_GEO_CONFIG";
    ErrorTypes["CHART_TYPE_DISABLED_BY_ADMIN"] = "CHART_TYPE_DISABLED_BY_ADMIN";
    ErrorTypes["INVALID_DATA_TOOLTIP_CONFIG"] = "INVALID_DATA_TOOLTIP_CONFIG";
    ErrorTypes["ONE_MANY_MAPPING_TOOLTIP_CONFIG"] = "ONE_MANY_MAPPING_TOOLTIP_CONFIG";
})(ErrorTypes || (ErrorTypes = {}));
function requiresUniqueXValuesPerSeries(chartType) {
    const exclusionList = [
        ChartType.Heatmap,
        ChartType.Scatter,
        ChartType.Bubble,
        ChartType.GeoEarthGraph,
        ChartType.Funnel,
        ChartType.PivotTable,
        ChartType.Kpi,
    ];
    return !exclusionList.includes(chartType);
}
function isInvalidTooltipConfig(dataPoint) {
    if (!dataPoint) {
        return false;
    }
    const tooltipDataContent = dataPoint.tooltipDataContent;
    const xRawValues = dataPoint.xRawValues;
    return xRawValues === null || xRawValues === void 0 ? void 0 : xRawValues.some(xRawValue => {
        return !!_.find(tooltipDataContent, { dataValue: xRawValue });
    });
}
function getSeriesXUniquenessValidationError(dataModel, chartModel, chartType, envFlags) {
    var _a;
    chartType = chartType || getChartType(chartModel);
    if (!requiresUniqueXValuesPerSeries(chartType)) {
        return null;
    }
    const series = getSeries(dataModel) || [];
    const chartPointXGetter = chartPoint => {
        return chartPoint.x;
    };
    for (let i = 0; i < series.length; i++) {
        const serie = series[i];
        if (arrayHasDuplicates(serie.data, chartPointXGetter)) {
            if ((envFlags === null || envFlags === void 0 ? void 0 : envFlags.isTooltipCustomizationEnabled) &&
                isInvalidTooltipConfig((_a = serie.data) === null || _a === void 0 ? void 0 : _a[0])) {
                return { type: ErrorTypes.INVALID_DATA_TOOLTIP_CONFIG };
            }
            return { type: ErrorTypes.UNIQUE_X_VALUES_PER_SERIES };
        }
    }
    return null;
}
function requiresUniqueXValuesAcrossAllData(chartType) {
    return isGeoChartType(chartType);
}
function checkForXDuplicateAcrossAllSeries(series) {
    const xValuesSet = {};
    return _.some(series, (serie) => {
        return _.some(serie.data, dataPoint => {
            if (_.has(xValuesSet, dataPoint.x)) {
                return true;
            }
            xValuesSet[dataPoint.x] = true;
            return false;
        });
    });
}
function getXUniquenessAcrossAllDataValidationError(dataModel, chartModel, chartType) {
    chartType = chartType || getChartType(chartModel);
    if (!requiresUniqueXValuesAcrossAllData(chartType)) {
        return null;
    }
    const series = getSeries(dataModel) || [];
    if (series.length <= 1) {
        return null;
    }
    if (!checkForXDuplicateAcrossAllSeries(series)) {
        return null;
    }
    return {
        type: ErrorTypes.UNIQUE_X_VALUES_ALL_DATA,
    };
}
const isValidOneToManyMappingError = (chartType) => {
    const exclusionList = [ChartType.Sankey, ChartType.PivotTable];
    return !exclusionList.includes(chartType);
};
const isColumnPartOfConfig = (colId, chartViz, envFlags) => {
    var _a;
    const tooltipConfigColumns = ((_a = getTooltipConfig(chartViz)) === null || _a === void 0 ? void 0 : _a.columnIds) || [];
    const conditionalFormattingColumns = getColumnsUsedInConditionalFormatting(chartViz);
    if (envFlags.isTooltipCustomizationEnabled &&
        tooltipConfigColumns.includes(colId)) {
        return true;
    }
    if (envFlags.isEnhancedCfEnabled &&
        conditionalFormattingColumns.includes(colId)) {
        return true;
    }
    return false;
};
export const generateAttributeColumnsKey = (attributeColumnDetailsArray, dataRow) => {
    return attributeColumnDetailsArray
        .map(columnDetailsObject => {
        var _a, _b, _c, _d;
        if (columnDetailsObject.hasCustomCalendar) {
            const customCalendarDate = dataRow[columnDetailsObject.orderIndex];
            return (((_a = getStartEpoch(customCalendarDate)) === null || _a === void 0 ? void 0 : _a.toString()) +
                ((_c = (_b = getEndEpoch(customCalendarDate)) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '') +
                ((_d = getDisplayString(customCalendarDate)) !== null && _d !== void 0 ? _d : ''));
        }
        return dataRow[columnDetailsObject.orderIndex];
    })
        .join('---');
};
const getOneToManyMappingError = (dataModel, chartViz, chartType, envFlags) => {
    let renderError = null;
    let oneToManyMappingColumns = [];
    if ((envFlags.isTooltipCustomizationEnabled ||
        envFlags.isEnhancedCfEnabled) &&
        !!isValidOneToManyMappingError(chartType)) {
        const dataArrayList = dataModel === null || dataModel === void 0 ? void 0 : dataModel.getDataArray();
        dataArrayList.forEach((dataArray, i) => {
            const allAxisColumns = getAllColumnsInAxisConfig(chartViz.config.axisConfig[i]);
            const allAxisColumnIds = allAxisColumns.map(col => col.id);
            const attributeAxisColumns = allAxisColumns.filter((col) => col.type === AnswerColumnEffectiveType.Attribute);
            const data = dataArray.getData();
            const columnsDataOrder = chartViz.data[i].columnDataLite.map(columnData => columnData.columnId);
            const attributeColumnDetailsArray = attributeAxisColumns.map(col => {
                return {
                    orderIndex: columnsDataOrder.indexOf(col.id),
                    hasCustomCalendar: hasCustomCalendar(col),
                };
            });
            const map = {};
            const faultyColumns = new Set();
            data.forEach(dataRow => {
                const attributeColumnsKey = generateAttributeColumnsKey(attributeColumnDetailsArray, dataRow);
                if (!map[attributeColumnsKey]) {
                    map[attributeColumnsKey] = dataRow;
                }
                else {
                    dataRow.forEach((dataPoint, index) => {
                        const existingDataPoint = map[attributeColumnsKey][index];
                        const colId = columnsDataOrder[index];
                        const column = getColumn(chartViz, colId);
                        if (isAttribute(column) &&
                            !allAxisColumnIds.includes(colId) &&
                            isColumnPartOfConfig(colId, chartViz, envFlags) &&
                            dataPoint !== existingDataPoint) {
                            faultyColumns.add(column.id);
                        }
                    });
                }
            });
            oneToManyMappingColumns = Array.from(faultyColumns);
            if (oneToManyMappingColumns === null || oneToManyMappingColumns === void 0 ? void 0 : oneToManyMappingColumns.length) {
                renderError = {
                    type: ErrorTypes.ONE_MANY_MAPPING_TOOLTIP_CONFIG,
                    info: {
                        oneToManyMappingColumns,
                    },
                };
            }
        });
    }
    return renderError;
};
export const getChartDataValidationError = (dataModel, chartModel, chartType) => {
    if (!chartModel || !dataModel || dataModel.hasNoData) {
        return null;
    }
    chartType = chartType || getChartType(chartModel);
    const oneToManyMappingError = getOneToManyMappingError(dataModel, chartModel, chartType, getEnvFlags());
    if (oneToManyMappingError) {
        return oneToManyMappingError;
    }
    const seriesXUniquenessError = getSeriesXUniquenessValidationError(dataModel, chartModel, chartType, getEnvFlags());
    if (seriesXUniquenessError) {
        return seriesXUniquenessError;
    }
    const allDataXUniquenessError = getXUniquenessAcrossAllDataValidationError(dataModel, chartModel, chartType);
    if (allDataXUniquenessError) {
        return allDataXUniquenessError;
    }
    const legendCardinalityError = getLegendCardinalityError(chartModel, dataModel);
    if (legendCardinalityError) {
        return legendCardinalityError;
    }
    const chartSpecificDataValidationErr = getChartSpecificDataValidationError(chartModel, dataModel);
    return chartSpecificDataValidationErr;
};
export const chartDataValidationTestHooks = {
    isColumnPartOfConfig,
};
//# sourceMappingURL=chart-data-validation.js.map